import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { selectPageSlice } from '../Selector';

// Must match index.scss variables
const PAGE_HEIGHT = 1123;
const PADDING_TOP = 64;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT; // 959px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

// Header labels whose data cells get a shaded background.
const SHADED_HEADERS = new Set(['SELF', 'ALL BUT SELF', 'ALL BUT SELF*', 'CONSENSUS']);
// Header labels whose data cell values render bold.
const BOLD_HEADERS = new Set(['CONSENSUS']);

// First two header cells are COMPETENCY / BEHAVIORS; the rest are data columns.
// Row keys are read directly from the header cell's `key` property.
function getDataColumns(headerRows) {
  const lastRow = headerRows[headerRows.length - 1] || [];
  return lastRow.slice(2).map((cell, idx) => {
    const label = (cell.content || '').toString().trim().toUpperCase();
    return {
      key: cell.key || null,
      styles: cell.styles,
      shaded: SHADED_HEADERS.has(label),
      bold: BOLD_HEADERS.has(label),
      idx,
    };
  });
}

function DimensionHeading({ dimension }) {
  return (
    <EditableText
      as="h2"
      className="pdf-section-heading"
      path={`standardDeviationSection[${dimension}].dimensionLabel`}
      variant="inline"
      stripInlineWrap
      fallback={(dimension || '').toUpperCase()}
    />
  );
}

function TableHead({ headerRows, theadProps }) {
  return (
    <thead {...theadProps}>
      {headerRows.map((cells, rowIdx) => (
        <tr key={rowIdx}>
          {cells.map((cell, cellIdx) => {
            const isCapability = cellIdx === 0;
            const isBehaviors = cellIdx === 1;
            const className = isCapability
              ? 'col-capability'
              : isBehaviors
                ? 'col-behaviors'
                : 'col-data-header';
            const style = cell.styles
              ? {
                  textAlign: cell.styles.halign,
                  verticalAlign: cell.styles.valign,
                }
              : undefined;
            return (
              <th key={cellIdx} className={className} style={style}>
                {isCapability || isBehaviors ? cell.content : <span>{cell.content}</span>}
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

function TableRow({ row, subCat, subRows, idx, dataColumns }) {
  const isGroupEnd = idx === subRows.length - 1;
  return (
    <tr className={isGroupEnd ? 'group-end' : undefined}>
      {idx === 0 && (
        <td rowSpan={subRows.length} className="col-capability">
          {subCat}
        </td>
      )}
      <td className="col-behaviors">{row.behaviour}</td>
      {dataColumns.map((col) => (
        <td
          key={col.idx}
          className="col-data"
          style={{
            ...(col.shaded ? { backgroundColor: '#F2F2F2' } : {}),
            ...(col.bold ? { fontWeight: 'bold' } : {}),
          }}
        >
          {col.key ? row[col.key] : ''}
        </td>
      ))}
    </tr>
  );
}

function TablePage({ dimension, entries, pageNumber, personName, headerRows, dataColumns }) {
  return (
    <PageWrapper pageNumber={pageNumber} personName={personName}>
      <DimensionHeading dimension={dimension} />
      <div className="std-table-wrapper">
        <table className="pdf-table pdf-table--appendix">
          <TableHead headerRows={headerRows} />
          <tbody>
            {entries.map(([subCat, subRows]) =>
              subRows.map((row, idx) => (
                <TableRow
                  key={`${subCat}-${idx}`}
                  row={row}
                  subCat={subCat}
                  subRows={subRows}
                  idx={idx}
                  dataColumns={dataColumns}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </PageWrapper>
  );
}

function StandardDeviationPage({ data, personName, pageNumber, slice }) {
  const { dimension, tableContents } = data;
  const rows = tableContents.contents;
  const headerRows = tableContents.headers || [];
  const dataColumns = getDataColumns(headerRows);

  const isFirstSliceRef = useRef(true);

  // Group rows by sub-category (capability)
  const groupedBySubCategory = {};
  rows.forEach(row => {
    const sub = row.subCategory;
    if (!groupedBySubCategory[sub]) groupedBySubCategory[sub] = [];
    groupedBySubCategory[sub].push(row);
  });
  const entries = Object.entries(groupedBySubCategory);

  const measureRef = useRef(null);
  const [pageEntryGroups, setPageEntryGroups] = useState(null);

  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPageEntryGroups(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pageEntryGroups) return;
    if (!measureRef.current) return;

    const container = measureRef.current;
    const titleEl = container.querySelector('[data-std="title"]');
    const theadEl = container.querySelector('[data-std="thead"]');

    const trEls = Array.from(container.querySelectorAll('tr[data-group]'));
    const groupHeights = {};
    trEls.forEach(tr => {
      const g = parseInt(tr.getAttribute('data-group'), 10);
      groupHeights[g] = (groupHeights[g] || 0) + tr.getBoundingClientRect().height;
    });

    const tableEl = container.querySelector('[data-std="table"]');

    const titleH = titleEl ? titleEl.getBoundingClientRect().height : 0;
    const theadH = theadEl ? theadEl.getBoundingClientRect().height : 0;
    const actualTableH = tableEl ? tableEl.getBoundingClientRect().height : 0;

    const sumOfRowH = Object.values(groupHeights).reduce((s, h) => s + h, 0);
    const tableOverhead = Math.max(0, actualTableH - theadH - sumOfRowH);

    const available = CONTENT_HEIGHT - titleH - theadH - tableOverhead;

    const allPages = [];
    let pageGroup = [];
    let usedH = 0;

    entries.forEach((entry, idx) => {
      const h = groupHeights[idx] || 0;

      if (pageGroup.length > 0 && usedH + h > available) {
        allPages.push(pageGroup);
        pageGroup = [entry];
        usedH = h;
      } else {
        pageGroup.push(entry);
        usedH += h;
      }
    });

    if (pageGroup.length > 0) {
      allPages.push(pageGroup);
    }

    setPageEntryGroups(allPages.length > 0 ? allPages : [entries]);
  }, [pageEntryGroups, entries]);

  // ── Phase 1: measure ───────────────────────────────────────────────────────
  if (!pageEntryGroups) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: CONTENT_WIDTH,
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div data-std="title">
          <DimensionHeading dimension={dimension} />
        </div>
        <div className="std-table-wrapper">
          <table data-std="table" className="pdf-table pdf-table--appendix">
            <TableHead headerRows={headerRows} theadProps={{ 'data-std': 'thead' }} />
            <tbody>
              {entries.map(([subCat, subRows], groupIdx) =>
                subRows.map((row, idx) => (
                  <tr
                    key={`${subCat}-${idx}`}
                    data-group={groupIdx}
                    className={idx === subRows.length - 1 ? 'group-end' : undefined}
                  >
                    {idx === 0 && (
                      <td rowSpan={subRows.length} className="col-capability">
                        {subCat}
                      </td>
                    )}
                    <td className="col-behaviors">{row.behaviour}</td>
                    {dataColumns.map((col) => (
                      <td
                        key={col.idx}
                        className="col-data"
                        style={{
                          ...(col.shaded ? { backgroundColor: '#F2F2F2' } : {}),
                          ...(col.bold ? { fontWeight: 'bold' } : {}),
                        }}
                      >
                        {col.key ? row[col.key] : ''}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </div>
    );
  }

  // ── Phase 2: render ────────────────────────────────────────────────────────
  return (
    <>
      {pageEntryGroups.map((pageEntries, idx) => (
        <TablePage
          key={idx}
          dimension={dimension}
          entries={pageEntries}
          pageNumber={pageNumber + idx}
          personName={personName}
          headerRows={headerRows}
          dataColumns={dataColumns}
        />
      ))}
    </>
  );
}

const mapStateToProps = (state, ownProps) => ({
  slice: selectPageSlice(
    state,
    'standardDeviationSection',
    ownProps.data && ownProps.data.dimension
  ),
});

export default connect(mapStateToProps)(StandardDeviationPage);
